import { useMapContext } from '@components/map/MapContext';
import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import useSupercluster from '@hooks/useSuperCluster';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { closePopupByMapIdAndType, updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import destination from '@turf/destination';
import { point } from '@turf/helpers';
import { checkIfPopupIsFromFeature, checkIfPopupIsFromFeatureValue, findPopupByType } from '@utils/popup.utils';
import { Feature, GeoJsonProperties } from 'geojson';
import { cloneDeep } from 'lodash';
import { LngLat, MapEventOf } from 'mapbox-gl';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import Supercluster, { PointFeature } from 'supercluster';

import { NotificationTypeEnum } from '@/types/data/data.types';
import { LayerNameEnum } from '@/types/map.types';
import { HistoryPopupData, InfoDataTypeEnum, PopupType, PopupTypeEnum, SensorInfoData } from '@/types/popup.types';

const CLUSTER_RADIUS_SCALE = 68;
const MAP_TILES_DIMENSION = 512;
const EARTH_CIRCUMFERENCE = 2 * Math.PI * 6378137;

const calculateClusterRadius = (numLeaves: number): number => {
  const baseRadius = CLUSTER_RADIUS_SCALE * (1 + 1 / Math.sin(Math.PI / numLeaves));
  return Math.max(baseRadius, 5);
};

export type ClusterProps = {
  PURPLE: number;
  ORANGE: number;
  GREEN: number;
  ORANGE_MAINTENANCE: number;
  GRAY: number;
  WHITE: number;
};

export const pixelsToMeters = (zoom: number, pixels: number, lat: number) => {
  const worldSizeInPixel = MAP_TILES_DIMENSION * Math.pow(2, zoom);
  const metersPerPixel = (Math.cos((lat * Math.PI) / 180) * EARTH_CIRCUMFERENCE) / worldSizeInPixel;

  return pixels * metersPerPixel;
};

export const useClusterSelect = (
  points: PointFeature<GeoJsonProperties>[],
  superclusterOptions: Supercluster.Options<GeoJsonProperties, ClusterProps>,
): [
  {
    selectedCluster: GeoJsonProperties | null;
    updatedClusters: PointFeature<GeoJsonProperties>[];
  },
  (string | number | undefined)[],
  Feature[],
] => {
  const { current: map } = useMap();
  const { mapId, viewState } = useMapContext();
  const zoom = viewState.zoom;
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);

  const sensorInfoPopup = findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR);
  const sensorHistoryPopup = findPopupByType(popups, PopupTypeEnum.HISTORY, NotificationTypeEnum.SENSOR);
  const roundPopup = findPopupByType(popups, PopupTypeEnum.ROUND);

  const { clusters, supercluster } = useSupercluster({
    points: points,
    options: superclusterOptions,
  });
  const [clusterState, setClusterState] = useState({
    selectedCluster: null as GeoJsonProperties | null,
    updatedClusters: [] as PointFeature<GeoJsonProperties>[],
    currentZoom: zoom,
  });
  const leaveIds = clusters
    .flatMap((cluster) => {
      if (supercluster && cluster.properties?.cluster_id) {
        return supercluster.getLeaves(cluster.properties.cluster_id);
      }
      return [];
    })
    .map((feature) => feature.properties?.id);
  const areLeavesDisplayed = leaveIds.some((leaveId) =>
    clusterState.updatedClusters.map((feature) => feature.properties?.id).includes(leaveId),
  );

  const updateClusterState = useCallback((newState: Partial<typeof clusterState>) => {
    setClusterState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const calculateLeafPositions = (
    leaves: Supercluster.PointFeature<GeoJsonProperties>[],
    clusterPosition: mapboxgl.LngLat,
    clusterRadiusInPixel: number,
    zoom: number,
  ): Supercluster.PointFeature<Supercluster.AnyProps>[] => {
    if (map) {
      const clusterRadiusInMeters = pixelsToMeters(zoom, clusterRadiusInPixel, clusterPosition.lat);
      const angleStep = 360 / Math.max(leaves.length, 1);
      const leafRadius = clusterRadiusInMeters / 2;
      const clusterCenter = point([clusterPosition.lng, clusterPosition.lat]);

      return leaves.map((leaf, index) => {
        const angle = angleStep * index;
        const leafPoint = destination(clusterCenter, leafRadius, angle, { units: 'meters' });
        const [lon, lat] = leafPoint.geometry.coordinates;

        const leafCoordinates = [Number.parseFloat(lon.toFixed(6)), Number.parseFloat(lat.toFixed(6))];

        [sensorInfoPopup, sensorHistoryPopup, roundPopup]
          .flatMap((popup) => (popup ? [popup] : []))
          .forEach((popup) => {
            if (checkIfPopupIsFromFeatureValue(popup, leaf.properties?.value)) {
              updatePopup({
                mapId,
                popup: {
                  ...popup,
                  data: {
                    ...(popup.data as SensorInfoData),
                    featureValue: {
                      ...(popup.data as SensorInfoData).featureValue,
                      openedCluster: true,
                      tempCoordinates: leafCoordinates,
                    },
                    sensorUniqueKeys: {
                      ...(popup.data as HistoryPopupData).sensorUniqueKeys,
                      openedCluster: true,
                      tempCoordinates: leafCoordinates,
                    },
                  } as SensorInfoData,
                } as PopupType,
              });
            }
          });

        return {
          ...leaf,
          geometry: {
            ...leaf.geometry,
            coordinates: leafCoordinates,
          },
          properties: {
            ...leaf.properties,
            clusterFeature: true,
            value: {
              ...leaf.properties?.value,
              openedCluster: true,
              tempCoordinates: leafCoordinates,
            },
          },
        };
      });
    }
    return [];
  };

  const updateClustersAndLeaves = (
    clickedFeature: PointFeature<GeoJsonProperties>,
    leaves: PointFeature<GeoJsonProperties>[],
    cluster_id: number,
  ) => {
    const clusterPosition = new LngLat(clickedFeature.geometry.coordinates[0], clickedFeature.geometry.coordinates[1]);
    const clusterRadius = calculateClusterRadius(leaves.length);

    const updatedClusters = clusters.map((feature) => {
      const isSelected = Boolean(feature.id === cluster_id || feature.properties?.cluster_id === cluster_id);

      return {
        ...feature,
        properties: {
          ...feature.properties,
          clusterRadius,
          selected: isSelected,
        },
      };
    });

    const updatedLeaves = calculateLeafPositions(leaves, clusterPosition, clusterRadius, zoom);
    setClusterState((prevState) => ({
      ...prevState,
      selectedCluster: clickedFeature,
      updatedClusters: cloneDeep([...updatedClusters, ...updatedLeaves]),
      currentZoom: zoom,
    }));
  };

  const handleOpenInfo = useCallback(
    (event: MapEventOf<'click'>, properties: GeoJsonProperties) => {
      if (properties) {
        const featureValue = JSON.parse(properties.value);
        const controlPosition = { x: event.originalEvent.clientX, y: event.originalEvent.clientY };
        updatePopup({
          mapId,
          popup: {
            type: PopupTypeEnum.INFO,
            position: controlPosition,
            data: {
              type: InfoDataTypeEnum.INFO_SENSOR,
              featureValue: featureValue,
            },
          },
        });
      }
    },
    [updatePopup, mapId],
  );

  const updateClusterView = useCallback(
    (event?: MapEventOf<'click'>) => {
      if (!map || !areLeavesDisplayed) return;
      const clickedFeature =
        event &&
        map.queryRenderedFeatures(event.point, {
          layers: [LayerNameEnum.SENSOR_CLUSTER_LEAVES, LayerNameEnum.SENSOR_CLUSTER_OVERLAY],
        })[0];
      if (clickedFeature?.properties && leaveIds.includes(clickedFeature.properties.id)) {
        if (sensorInfoPopup && checkIfPopupIsFromFeature(sensorInfoPopup, clickedFeature)) {
          closePopupByType({ mapId, type: PopupTypeEnum.INFO, dataType: InfoDataTypeEnum.INFO_SENSOR });
        } else {
          handleOpenInfo(event!, clickedFeature.properties);
        }
        return;
      }
      if (clusterState.currentZoom !== zoom || !clickedFeature) {
        for (const popup of [sensorInfoPopup, sensorHistoryPopup, roundPopup].filter(Boolean)) {
          updatePopup({
            mapId,
            popup: {
              ...popup!,
              data: {
                ...(popup!.data as SensorInfoData),
                featureValue: {
                  ...(popup!.data! as SensorInfoData).featureValue,
                  openedCluster: false,
                },
                sensorUniqueKeys: {
                  ...(popup!.data! as HistoryPopupData).sensorUniqueKeys,
                  openedCluster: false,
                },
              } as SensorInfoData | HistoryPopupData,
            } as PopupType,
          });
        }
        updateClusterState({
          selectedCluster: null,
          updatedClusters: [],
          currentZoom: zoom,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areLeavesDisplayed, clusterState.currentZoom, handleOpenInfo, leaveIds, map, updateClusterState, zoom],
  );

  const handleClick = (event: MapEventOf<'click'>) => {
    if (!map || !supercluster || zoom < 13) return;
    const clickedFeature = event.features?.[0];
    if (clickedFeature?.geometry.type !== 'Point') return;

    // Prevent click propagation, if a target is selected, do not select the cluster underneath
    const targetLayerFeatures = map.queryRenderedFeatures(event.point, { layers: [LayerNameEnum.TARGETS] });
    if (targetLayerFeatures.length) {
      return;
    }

    const props = clickedFeature.properties;
    if (!props?.cluster || props.cluster_id == null) {
      return;
    }
    const leaves = supercluster.getLeaves(props.cluster_id);

    if (leaves.length >= 2 && leaves.length <= 5) {
      updateClustersAndLeaves(clickedFeature as PointFeature<GeoJsonProperties>, leaves, props.cluster_id);
    }
  };

  useMapLayerMouseEvent('click', handleClick, LayerNameEnum.SENSOR_CLUSTERS);
  useMapLayerMouseEvent('click', updateClusterView);

  useEffect(() => {
    if (areLeavesDisplayed && clusterState.currentZoom !== zoom) {
      updateClusterView();
    }
  }, [areLeavesDisplayed, clusterState.currentZoom, map, updateClusterView, zoom]);

  return [clusterState, leaveIds, clusters];
};
